import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import SearchField from '../../General/Dialogs/UploadDialog/_parts/SearchField';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getAreasExtendedConfigByField } from '../../../redux/project/sliceProjectAreas';
import { debounce } from 'lodash';
import {
    getProjectAdvancedSearchDocuments,
    setAreaSearchText,
} from '../../../redux/project/documents/sliceProjectAdvancedSearch';
import { IAppReduxStore } from '../../../redux/store';
import { mdiDatabaseSearchOutline, mdiTableSearch, mdiTune } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import { getCurrentView } from '../../../redux/project/sliceProjectViews';
import {
    revertDocuments,
    setDocuments,
    stashDocuments,
} from '../../../redux/project/documents/sliceProjectDocuments';
import { updateSelectedDocuments } from '../../../redux/project/documents/sliceProjectSelectedDocuments';
import { getAllFields } from '../../../util/DataUtils';
import { getCurrentFieldsOrFieldsSchema } from '../../../redux/project/sliceProjectViewData';
import { toBeginLowerCase, toBeginUpperCase } from '../../../util/StringUtils';
import AppTooltip from '../../General/AppTooltip/AppTooltip';
import { Box } from '@mui/material';

const config = {
    debounceMs: 200,
    options: {
        table: {
            locale: 'searchInTable',
            icon: mdiTableSearch,
            autoSearch: true,
            serversideSearch: false,
        },
        area: {
            locale: 'searchInArea',
            icon: mdiDatabaseSearchOutline,
            autoSearch: false,
            serversideSearch: true,
        },
    },
};

export default () => {
    const { replicaId, areaName, viewName } = useParams();
    const dispatch: Dispatch<unknown> = useDispatch();
    const { t } = useTranslation('translations', { keyPrefix: 'projectAdvancedSearch' });

    const [searchText, setSearchText] = useState<string>('');
    const [searchOption, setSearchOption] = useState<string>(Object.keys(config.options)[0]);
    const [autoSearch, setAutoSearch] = useState<boolean>(config.options[searchOption].autoSearch);
    const [resetToken, setResetToken] = useState<number>(0);

    const searchLoading = useSelector(
        (_store: IAppReduxStore) => _store.projectAdvancedSearch.loading,
    );
    const { dataToSave } = useSelector((store: IAppReduxStore) => store.projectViewData);
    const view = useSelector(getCurrentView({ areaName, viewName }));
    const fields = useSelector((_store: IAppReduxStore) => _store.projectViewData.fields.data);
    const customFields = useSelector(getCurrentFieldsOrFieldsSchema('fields'));
    const areaNavigationOptions = useSelector(
        getAreasExtendedConfigByField(areaName, 'NavigationOptions'),
    );
    const { data: documents, stash } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments,
    );

    const visibleCustomFields = useMemo(() => {
        return customFields.filter(field => field.type === 'string' && field.visible);
    }, [customFields]);

    const onSearchTextChanged = debounce(
        (newSearchText: string) => setSearchText(newSearchText),
        config.debounceMs,
    );

    const onSearchOptionChanged = debounce(
        (newSearchOption: string) => setSearchOption(newSearchOption),
        config.debounceMs,
    );

    const clientsideSearch = () => {
        const searchTextLowerCase = searchText.toLowerCase();
        const results = (stash.length === 0 ? documents : stash)?.filter(x =>
            visibleCustomFields.some(field => {
                if (typeof x[field.field] !== 'string') return false;

                return (
                    x[field.field] ||
                    x[toBeginUpperCase(field.field)] ||
                    x[toBeginLowerCase(field.field)]
                )
                    ?.toLowerCase()
                    ?.includes(searchTextLowerCase);
            }),
        );

        if (stash.length === 0) {
            dispatch(stashDocuments());
        }
        dispatch(setDocuments(results));
    };

    const serversideSearch = async () => {
        dispatch(setAreaSearchText(searchText));
        dispatch(
            getProjectAdvancedSearchDocuments({
                replicaId,
                viewName: view.viewStructure,
                searchTerm: searchText,
                fieldset: getAllFields(fields).join(';'),
            }),
        );
    };

    const onSearchSubmit = () => {
        if (searchText) {
            dispatch(
                updateSelectedDocuments({
                    replicaId,
                    areaName,
                    documentIds: [],
                }),
            );

            config.options[searchOption].serversideSearch ? serversideSearch() : clientsideSearch();
        }
    };

    useEffect(() => {
        if (!searchText && stash.length > 0) {
            dispatch(revertDocuments());
        }

        if (autoSearch) {
            onSearchSubmit();
        }
    }, [searchText]);

    useEffect(() => {
        setAutoSearch(config.options[searchOption].autoSearch);
        onSearchSubmit();
    }, [searchOption]);

    useEffect(() => {
        setSearchText('');
        setSearchOption(Object.keys(config.options)[0]);
        setResetToken(Date.now());
    }, [view]);

    return areaNavigationOptions?.includes('SearchBox') ? (
        <AppTooltip title={!!dataToSave && t('searchDisabled')}>
            <Box sx={{ alignContent: 'center' }}>
                <SearchField
                    onChange={onSearchTextChanged}
                    placeholder={t(config.options[searchOption].locale)}
                    loading={searchLoading}
                    optionsConfig={{
                        icon: mdiTune,
                        options: Object.keys(config.options).map(key => ({
                            key: key,
                            label: t(config.options[key].locale),
                            icon: config.options[key].icon,
                        })),
                        onOptionSelect: onSearchOptionChanged,
                    }}
                    onSubmit={autoSearch ? undefined : onSearchSubmit}
                    resetToken={resetToken}
                    isDisabled={!!dataToSave}
                />
            </Box>
        </AppTooltip>
    ) : (
        <></>
    );
};
